

import React, { useEffect, useState } from "react";
import {
    Box,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Button,
    IconButton,
    Tooltip,
    Menu,
    MenuItem,
    Checkbox,
    Stack,
    CircularProgress,
    TextField,
    Divider,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import ViewWeekIcon from "@mui/icons-material/ViewWeek";
import SortIcon from "@mui/icons-material/Sort";
import Close from '@mui/icons-material/Close';
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";
import dayjs from "dayjs"; // if you use it, otherwise plain Date

import { useProductionSummaryToday } from "../../../hooks/production/useProductionSummaryToday";
import CustomTableContainer from "../../../Layout/CustomTableContainer";
import { tokens } from "../../../theme/theme";
import SkeletonTableLoader from "../../../components/table/SkeletonTableLoader";

/* --------------------------------------------------
   SAMPLE TODAY DATA (fallback)
-------------------------------------------------- */
const sampleTodayData = [
    { r_shift_code: "A", jumbo_qty: 85.4, rewinder_total: 93.1, reel_qty: 40.6, sheeter_qty: 52.5, reel_weighment_qty: 40.6, ream_qty: 74.6, bundle_qty: 27.5, lodge_reel_qty: 40.6, lodge_bundle_qty: 0, lodge_ream_pallet_qty: 0, total_lodged: 40.6 },
    { r_shift_code: "B", jumbo_qty: 0, rewinder_total: 11.6, reel_qty: 11.6, sheeter_qty: 11.6, reel_weighment_qty: 0, ream_qty: 32.5, bundle_qty: 15, lodge_reel_qty: 0, lodge_bundle_qty: 5.3, lodge_ream_pallet_qty: 0, total_lodged: 5.3 },
    { r_shift_code: "C", jumbo_qty: 0, rewinder_total: 0, reel_qty: 0, sheeter_qty: 0, reel_weighment_qty: 0, ream_qty: 0, bundle_qty: 0, lodge_reel_qty: 0, lodge_bundle_qty: 0, lodge_ream_pallet_qty: 0, total_lodged: 0 },
    { r_shift_code: "D", jumbo_qty: 0, rewinder_total: 0, reel_qty: 0, sheeter_qty: 0, reel_weighment_qty: 0, ream_qty: 0, bundle_qty: 0, lodge_reel_qty: 0, lodge_bundle_qty: 0, lodge_ream_pallet_qty: 0, total_lodged: 0 },
];

/* --------------------------------------------------
   SAMPLE MONTH DATA (fallback)
-------------------------------------------------- */
const sampleMonthData = [
    { year: 2025, month: 1, order_qty: 1204.5, deckle_qty: 0, jumbo_qty: 0, rewinder_total: 0, rewinder_reel: 0, rewinder_sheet: 0, reel_weighment_qty: 0, sheeter_qty: 3605.2, ream_qty: 0, bundle_qty: 0, lodge_reel_qty: 0, lodge_bundle_qty: 0, lodge_ream_pallet_qty: 0, total_lodged: 0 },
    { year: 2025, month: 2, order_qty: 311, deckle_qty: 0, jumbo_qty: 0, rewinder_total: 0, rewinder_reel: 0, rewinder_sheet: 0, reel_weighment_qty: 0, sheeter_qty: 0, ream_qty: 0, bundle_qty: 0, lodge_reel_qty: 0, lodge_bundle_qty: 0, lodge_ream_pallet_qty: 0, total_lodged: 0 },
    { year: 2025, month: 3, order_qty: 5956.5, deckle_qty: 5595, jumbo_qty: 6194.6, rewinder_total: 5652.5, rewinder_reel: 1347.2, rewinder_sheet: 4305.3, reel_weighment_qty: 1347.2, sheeter_qty: 3605.2, ream_qty: 3311.4, bundle_qty: 3045.2, lodge_reel_qty: 273.3, lodge_bundle_qty: 1143.5, lodge_ream_pallet_qty: 121.9, total_lodged: 4155.2 },
    // ... keep all other months you had (Apr–Nov)
];

/* --------------------------------------------------
   TODAY TRANSFORM
-------------------------------------------------- */
const transformTodayData = (rows) => {
    const output = {};

    rows.forEach((r) => {
        if (!output["Order Qty"]) output["Order Qty"] = { production: "Order Qty", A: "", B: "", C: "", D: "" };
        if (!output["Deckle Qty"]) output["Deckle Qty"] = { production: "Deckle Qty", A: "", B: "", C: "", D: "" };
        if (!output["Jumbo"]) output["Jumbo"] = { production: "Jumbo", A: 0, B: 0, C: 0, D: 0 };
        if (!output["Rewinder Total"]) output["Rewinder Total"] = { production: "Rewinder Total", A: 0, B: 0, C: 0, D: 0 };
        if (!output["Reel"]) output["Reel"] = { production: "Reel", A: 0, B: 0, C: 0, D: 0 };
        if (!output["Sheeter"]) output["Sheeter"] = { production: "Sheeter", A: 0, B: 0, C: 0, D: 0 };
        if (!output["Ream"]) output["Ream"] = { production: "Ream", A: 0, B: 0, C: 0, D: 0 };
        if (!output["Bundle"]) output["Bundle"] = { production: "Bundle", A: 0, B: 0, C: 0, D: 0 };
        if (!output["Ream Pallet"]) output["Ream Pallet"] = { production: "Ream Pallet", A: 0, B: 0, C: 0, D: 0 };
        if (!output["Lodged - Reel"]) output["Lodged - Reel"] = { production: "Lodged - Reel", A: 0, B: 0, C: 0, D: 0 };
        if (!output["Lodged - Bundle"]) output["Lodged - Bundle"] = { production: "Lodged - Bundle", A: 0, B: 0, C: 0, D: 0 };
        if (!output["Lodged - Ream Pallet"]) output["Lodged - Ream Pallet"] = { production: "Lodged - Ream Pallet", A: 0, B: 0, C: 0, D: 0 };
        if (!output["Total Lodged"]) output["Total Lodged"] = { production: "Total Lodged", A: 0, B: 0, C: 0, D: 0 };

        const s = r.r_shift_code; // A/B/C/D

        output["Order Qty"][s] = "";
        output["Deckle Qty"][s] = "";
        output["Jumbo"][s] = r.jumbo_qty;
        output["Rewinder Total"][s] = r.rewinder_total;
        output["Reel"][s] = r.reel_qty;
        output["Sheeter"][s] = r.sheeter_qty;
        output["Ream"][s] = r.ream_qty;
        output["Bundle"][s] = r.bundle_qty;
        output["Ream Pallet"][s] = r.ream_pallet_qty;
        output["Lodged - Reel"][s] = r.lodge_reel_qty;
        output["Lodged - Bundle"][s] = r.lodge_bundle_qty;
        output["Lodged - Ream Pallet"][s] = r.lodge_ream_pallet_qty;
        output["Total Lodged"][s] = r.total_lodged;
    });

    return Object.values(output).map((r) => {
        const skip =
            r.production === "Order Qty" ||
            r.production === "Deckle Qty";
        return {
            ...r,
            total: skip ? "" : (r.A + r.B + r.C + r.D).toFixed(1),
        };
    });
};

/* --------------------------------------------------
   MONTH TRANSFORM
-------------------------------------------------- */
const transformMonthData = (rows) => {
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const monthMap = {};
    rows.forEach((r) => {
        monthMap[months[r.month - 1]] = r;
    });

    const productions = [

        { key: "order_qty", label: "Order Qty" },
        { key: "deckle_qty", label: "Deckle Qty" },
        { key: "jumbo_qty", label: "Jumbo" },
        { key: "rewinder_total", label: "Rewinder Total" },
        { key: "reel_weighment_qty", label: "Reel" },
        { key: "sheeter_qty", label: "Sheeter" },
        { key: "ream_qty", label: "Ream" },
        { key: "bundle_qty", label: "Bundle" },
        { key: "ream_pallet_qty", label: "Ream Pallet" },
        { key: "lodge_reel_qty", label: "Lodged - Reel" },
        { key: "lodge_bundle_qty", label: "Lodged - Bundle" },
        { key: "lodge_ream_pallet_qty", label: "Lodged - Ream Pallet" },
        { key: "total_lodge", label: "Total Lodged" }
    ];

    return productions.map((p) => {
        const row = { production: p.label };
        // Only Jan–Nov like your UI
        ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"].forEach((m) => {
            row[m] = monthMap[m] ? monthMap[m][p.key] : 0;
        });
        return row;
    });
};

/* --------------------------------------------------
   MERGE TODAY + MONTH
-------------------------------------------------- */
const mergeTables = (today, month) => {
    return today.map((t) => {
        const m = month.find((mm) => mm.production === t.production);
        return { ...t, ...(m || {}) };
    });
};

/* --------------------------------------------------
   MAIN COMPONENT
-------------------------------------------------- */
export default function ProductionSummary({ handleBackWinderMachineEntry }) {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const mode = theme.palette.mode;

    const [rows, setRows] = useState([]);
    const [isDataLoad, setIsDataLoad] = useState(false);

    const { summaryToday, fetchTodayProductionSummary, summaryMonthWise, isLoading } = useProductionSummaryToday();

    // for sorting and column visibility
    const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
    const [columnMenuAnchor, setColumnMenuAnchor] = useState(null);
    const [exportLoading, setExportLoading] = useState(false);

    // component body:
    const [fromDate, setFromDate] = useState(() =>
        new Date(new Date().getFullYear(), new Date().getMonth(), 1)
            .toISOString()
            .slice(0, 10)
    );
    const [toDate, setToDate] = useState(() =>
        new Date().toISOString().slice(0, 10)
    );

    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const [monthHeaders, setMonthHeaders] = useState(
        months.map((m) => `${m} 2025`)
    );

    const [visibleColumns, setVisibleColumns] = useState(() => {
        const base = { Production: true, A: true, B: true, C: true, D: true, Total: true };
        months.forEach((m) => { base[m] = true; });
        return base;
    });

    const tableColors = {
        headerBg: mode === "dark" ? theme.palette.primary[600] || "#1F2A40" : theme.palette.grey[100],
        todayGroup: mode === "dark" ? theme.palette.orangeAccent?.[400] || "#ff8a46" : theme.palette.yellowAccent?.[100] || "#fff7cc",
        monthGroup: mode === "dark" ? theme.palette.purpleAccent?.[400] || "#a27fe3" : theme.palette.purpleAccent?.[100] || "#e8dff8",
        shiftHeader: mode === "dark" ? theme.palette.grey[700] : theme.palette.grey[200],
        productionCol: mode === "dark" ? theme.palette.blueAccent?.[800] || "#1565c0" : theme.palette.blueAccent?.[100] || "#bbdefb",
        totalCol: mode === "dark" ? theme.palette.greenAccent?.[400] || "#3da58a" : theme.palette.greenAccent?.[100] || "#dbf5ee",
        rowBg: mode === "dark" ? theme.palette.grey[800] : "#fafafa",
    };

    /* ---- Load Today only once ---- */



    useEffect(() => {
        const loadData = async () => {
            if (!isDataLoad) {
                let company = localStorage.getItem("companyid");
                await fetchTodayProductionSummary({
                    company,
                    fromDate,
                    toDate,
                });
                // if month-wise uses separate call:
                // await fetchMonthWiseSummary({ fromDate, toDate });
                setIsDataLoad(true);
            }
        };
        loadData();
    }, [isDataLoad, fetchTodayProductionSummary, fromDate, toDate]);




    /* ---- Build rows when both summaries ready ---- */
    useEffect(() => {
        if (summaryToday == null || summaryMonthWise == null) return;

        const todayRaw = summaryToday?.data ?? summaryToday;
        const monthRaw = summaryMonthWise?.data ?? summaryMonthWise;

        const today = transformTodayData(
            todayRaw && todayRaw.length > 0 ? todayRaw : sampleTodayData
        );
        const month = transformMonthData(
            monthRaw && monthRaw.length > 0 ? monthRaw : sampleMonthData
        );

        const merged = mergeTables(today, month);
        setRows(merged);

        const year = (monthRaw && monthRaw[0]?.year) || 2025;
        setMonthHeaders(months.map((m) => `${m} ${year}`));
    }, [summaryToday, summaryMonthWise]);

    /* ---- Sorting ---- */
    const handleSort = (key) => {
        let direction = "asc";
        if (sortConfig.key === key && sortConfig.direction === "asc") {
            direction = "desc";
        }
        setSortConfig({ key, direction });

        const sorted = [...rows].sort((a, b) => {
            const aVal = a[key];
            const bVal = b[key];

            const aNum = typeof aVal === "string" ? Number(aVal) : aVal;
            const bNum = typeof bVal === "string" ? Number(bVal) : bVal;

            if (!isNaN(aNum) && !isNaN(bNum)) {
                return direction === "asc" ? aNum - bNum : bNum - aNum;
            }
            if (typeof aVal === "string" && typeof bVal === "string") {
                return direction === "asc" ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
            }
            return 0;
        });

        setRows(sorted);
    };

    /* ---- Column visibility ---- */
    const handleColumnToggle = (name) => {
        setVisibleColumns((prev) => ({ ...prev, [name]: !prev[name] }));
    };

    const handleColumnMenuOpen = (e) => setColumnMenuAnchor(e.currentTarget);
    const handleColumnMenuClose = () => setColumnMenuAnchor(null);

    /* ---- Export Excel ---- */
    const handleExportExcel = () => {
        setExportLoading(true);
        try {
            // Use monthHeaders with year instead of just months
            const header1 = ["Production", "A", "B", "C", "D", "Total", ...monthHeaders];
            const dataRows = rows.map((r) => [
                r.production,
                r.A,
                r.B,
                r.C,
                r.D,
                r.total,
                ...months.map((m) => r[m] ?? 0), // still read from months object keys
            ]);

            const ws = XLSX.utils.aoa_to_sheet([header1, ...dataRows]);

            // Set column widths
            ws["!cols"] = Array(7 + months.length).fill({ wch: 18 });

            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, "Production");
            XLSX.writeFile(wb, `ProductionReport_${new Date().toISOString().slice(0, 10)}.xlsx`);
        } finally {
            setExportLoading(false);
        }
    };

    /* ---- Export PDF ---- */
    const handleExportPDF = () => {
        setExportLoading(true);
        try {
            const doc = new jsPDF("l", "mm", "a4");
            const pageWidth = doc.internal.pageSize.width;

            doc.setFontSize(14);
            doc.text("Production Report", pageWidth / 2, 12, { align: "center" });
            doc.setFontSize(9);
            doc.text(`Generated: ${new Date().toLocaleString()}`, pageWidth / 2, 18, { align: "center" });

            // Use monthHeaders with year instead of just months
            const head = [["Production", "A", "B", "C", "D", "Total", ...monthHeaders]];
            const body = rows.map((r) => [
                r.production,
                r.A,
                r.B,
                r.C,
                r.D,
                r.total,
                ...months.map((m) => r[m] ?? 0), // still read from months object keys
            ]);

            doc.autoTable({
                head,
                body,
                startY: 24,
                theme: "grid",
                headStyles: {
                    fillColor: mode === "dark" ? [31, 42, 64] : [179, 229, 252],
                    textColor: mode === "dark" ? 255 : 0,
                    fontStyle: "bold",
                },
                bodyStyles: {
                    fillColor: mode === "dark" ? [33, 33, 33] : [250, 250, 250],
                    textColor: mode === "dark" ? 255 : 0,
                },
                alternateRowStyles: {
                    fillColor: mode === "dark" ? [55, 55, 55] : [255, 255, 255],
                },
                columnStyles: {
                    0: { cellWidth: 30 },
                },
                margin: { left: 8, right: 8 },
                didDrawPage: (data) => {
                    const pageCount = doc.internal.getNumberOfPages();
                    doc.setFontSize(8);
                    doc.text(
                        `Page ${data.pageNumber} of ${pageCount}`,
                        pageWidth / 2,
                        doc.internal.pageSize.height - 8,
                        { align: "center" }
                    );
                },
            });

            doc.save(`ProductionReport_${new Date().toISOString().slice(0, 10)}.pdf`);
        } finally {
            setExportLoading(false);
        }
    };

    const shiftCodes = ["A", "B", "C", "D"];

    return (
        <Box sx={{ p: 2 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
                
                <Stack direction="row" spacing={2} alignItems="center">
                    <Box component="h2" sx={{ m: 0 }}>
                        Production Report
                    </Box>
                    <TextField
                        type="date"
                        size="small"
                        label="From"
                        value={fromDate}
                        onChange={(e) => setFromDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                        type="date"
                        size="small"
                        label="To"
                        value={toDate}
                        onChange={(e) => setToDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                    />
                    <Button
                        variant="outlined"
                        size="small"
                        color="secondary"
                        onClick={() => setIsDataLoad(false)} // trigger refetch with new range
                    >
                        Apply
                    </Button>
                </Stack>
                <Stack direction="row" spacing={1}>
                    <Tooltip title="Export Excel">
                        <Button
                            variant="contained"
                            size="small"
                            startIcon={<FileDownloadIcon />}
                            onClick={handleExportExcel}
                            disabled={exportLoading}
                        >
                            {exportLoading ? <CircularProgress size={18} /> : "Excel"}
                        </Button>
                    </Tooltip>
                    <Tooltip title="Export PDF">
                        <Button
                            variant="contained"
                            size="small"
                            startIcon={<FileDownloadIcon />}
                            onClick={handleExportPDF}
                            disabled={exportLoading}
                        >
                            {exportLoading ? <CircularProgress size={18} /> : "PDF"}
                        </Button>
                    </Tooltip>
                    <Tooltip title="Show / Hide Columns">
                        <IconButton size="small" onClick={handleColumnMenuOpen}>
                            <ViewWeekIcon />
                        </IconButton>
                    </Tooltip>
                    <Menu
                        anchorEl={columnMenuAnchor}
                        open={Boolean(columnMenuAnchor)}
                        onClose={handleColumnMenuClose}
                    >
                        {["Production", ...shiftCodes, "Total", ...months].map((name) => (
                            <MenuItem key={name} onClick={() => handleColumnToggle(name)}>
                                <Checkbox checked={visibleColumns[name]} size="small" />
                                {name}
                            </MenuItem>
                        ))}
                    </Menu>

                    <Tooltip title="close">
                        <IconButton size="small" onClick={handleBackWinderMachineEntry}>
                            <Close />
                        </IconButton>
                    </Tooltip>
                </Stack>
            </Box>
            <Divider />
            <Box sx={{ overflowX: "auto" }}>
                <CustomTableContainer colors={colors}>
                    {isLoading ? (
                        <SkeletonTableLoader rows={14} columns={17} />
                    ) : (
                        <Table sx={{ minWidth: 1400 }}>
                            <TableHead>
                                {/* Row 1: group headers */}
                                <TableRow sx={{ bgcolor: tableColors.headerBg }}>
                                    <TableCell />
                                    <TableCell
                                        colSpan={5}
                                        align="center"
                                        sx={{ bgcolor: tableColors.todayGroup, fontWeight: 600 }}
                                    >
                                        Today
                                    </TableCell>
                                    <TableCell
                                        colSpan={months.length}
                                        align="center"
                                        sx={{ bgcolor: tableColors.monthGroup, fontWeight: 600 }}
                                    >
                                        Month wise Detail
                                    </TableCell>
                                </TableRow>

                                {/* Row 2: column headers */}
                                <TableRow sx={{ bgcolor: tableColors.headerBg }}>
                                    {visibleColumns.Production && (
                                        <TableCell sx={{ fontWeight: 600 }}>
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                                Production
                                                <IconButton size="small" onClick={() => handleSort("production")}>
                                                    <SortIcon fontSize="inherit" />
                                                </IconButton>
                                            </Box>
                                        </TableCell>
                                    )}

                                    {shiftCodes.map(
                                        (s) =>
                                            visibleColumns[s] && (
                                                <TableCell key={s} sx={{ bgcolor: tableColors.shiftHeader }}>
                                                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                                        {s}
                                                        <IconButton size="small" onClick={() => handleSort(s)}>
                                                            <SortIcon fontSize="inherit" />
                                                        </IconButton>
                                                    </Box>
                                                </TableCell>
                                            )
                                    )}

                                    {visibleColumns.Total && (
                                        <TableCell sx={{ bgcolor: tableColors.shiftHeader, fontWeight: 600 }}>
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                                Total
                                                <IconButton size="small" onClick={() => handleSort("total")}>
                                                    <SortIcon fontSize="inherit" />
                                                </IconButton>
                                            </Box>
                                        </TableCell>
                                    )}

                                    {months.map(
                                        (m, i) =>
                                            visibleColumns[m] && (
                                                <TableCell key={m} sx={{ bgcolor: tableColors.shiftHeader, fontSize: "0.75rem" }}>
                                                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                                        {monthHeaders[i]}
                                                        <IconButton size="small" onClick={() => handleSort(m)}>
                                                            <SortIcon fontSize="inherit" />
                                                        </IconButton>
                                                    </Box>
                                                </TableCell>
                                            )
                                    )}
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {rows.map((r) => (
                                    <TableRow key={r.production} sx={{ bgcolor: tableColors.rowBg }}>
                                        {visibleColumns.Production && (
                                            <TableCell
                                                sx={{
                                                    bgcolor: tableColors.productionCol,
                                                    fontWeight: 600,
                                                }}
                                            >
                                                {r.production}
                                            </TableCell>
                                        )}

                                        {shiftCodes.map(
                                            (s) =>
                                                visibleColumns[s] && <TableCell key={s}>{r[s]}</TableCell>
                                        )}

                                        {visibleColumns.Total && (
                                            <TableCell
                                                sx={{
                                                    bgcolor: tableColors.totalCol,
                                                    fontWeight: 600,
                                                }}
                                            >
                                                {r.total}
                                            </TableCell>
                                        )}

                                        {months.map(
                                            (m) =>
                                                visibleColumns[m] && (
                                                    <TableCell key={m}>{r[m] ?? 0}</TableCell>
                                                )
                                        )}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </CustomTableContainer>
            </Box>
        </Box>
    );
}
